import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { EditorialImage } from './ui/EditorialImage';
import { useAddCartItem } from '../hooks/useCart';
import { useCurrency } from '../hooks/useCurrency';
import type { CatalogProduct } from '../lib/catalogTypes';

export function QuickViewModal({
  product,
  onClose,
}: {
  product: CatalogProduct | null;
  onClose: () => void;
}) {
  const [quantity, setQuantity] = useState(1);
  const addCartItem = useAddCartItem();
  const { format } = useCurrency();

  useEffect(() => {
    setQuantity(1);
  }, [product]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!product) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close quick view"
        onClick={onClose}
        className="absolute inset-0 bg-ink/60 backdrop-blur-sm"
      />
      <div className="relative grid w-full max-w-3xl gap-0 bg-cream shadow-2xl md:grid-cols-2">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center bg-cream/90 text-ink"
        >
          ×
        </button>

        <EditorialImage
          src={product.images?.[0]?.path}
          seed={`product-${product.slug}`}
          alt={product.name}
          duotone={false}
          aspect="aspect-square md:aspect-auto md:h-full"
          size="large"
        />

        <div className="flex flex-col justify-center p-7 md:p-9">
          <h2 className="font-serif text-2xl font-medium text-ink">{product.name}</h2>
          <p className="mt-3 text-lg font-semibold text-ink">{format(product.base_price)}</p>

          {product.requires_configuration ? (
            <div className="mt-6">
              <p className="mb-3 text-xs text-stone">This product requires customisation before purchase.</p>
              <Link
                to={`/products/${product.slug}`}
                onClick={onClose}
                className="block w-full bg-ink px-5 py-2.5 text-center text-[12px] font-medium uppercase tracking-[0.12em] text-cream transition-colors hover:bg-clay"
              >
                Select Options
              </Link>
            </div>
          ) : (
            <>
              <div className="mt-6 flex items-stretch gap-3">
                <div className="flex items-center border border-stone-light">
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="px-3 py-2.5 text-stone hover:text-ink"
                    aria-label="Decrease quantity"
                  >
                    −
                  </button>
                  <span className="w-8 text-center text-sm">{quantity}</span>
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => q + 1)}
                    className="px-3 py-2.5 text-stone hover:text-ink"
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => addCartItem.mutate({ product_id: product.id, quantity })}
                  disabled={addCartItem.isPending}
                  className="flex-1 bg-ink px-5 py-2.5 text-[12px] font-medium uppercase tracking-[0.12em] text-cream transition-colors hover:bg-clay disabled:opacity-50"
                >
                  {addCartItem.isPending ? 'Adding…' : 'Add to cart'}
                </button>
              </div>
              {addCartItem.isSuccess && (
                <p className="mt-3 text-xs text-moss">Added to your cart.</p>
              )}
            </>
          )}

          <Link
            to={`/products/${product.slug}`}
            onClick={onClose}
            className="mt-5 text-xs font-medium uppercase tracking-[0.1em] text-stone underline underline-offset-4 hover:text-ink"
          >
            View full details
          </Link>
        </div>
      </div>
    </div>
  );
}
